/**
 * Fait : l'accès du client aux données de l'API — chaque appel envoie le
 *   cookie de session et traduit une erreur du produit en objet lisible. Une
 *   surface qui a besoin de données appelle ces fonctions.
 *
 *   Quatre appels se font SANS session — connexion, oubli, nouveau mot de
 *   passe, inscription. Ils passent par ici comme les autres : `appelle`
 *   envoie le cookie s'il existe et ne s'en préoccupe pas autrement. Les
 *   laisser dehors aurait fait quatre `fetch` à la main, et autant d'endroits
 *   où oublier la traduction d'un refus.
 *
 * Ne fait pas : il ne décide d'aucun droit et n'affiche rien. Ce que le
 *   serveur refuse, il le rend tel quel — le client ne cache jamais un refus
 *   derrière une jolie phrase inventée.
 */

import type { NiveauDeMembre, RoleDeCompte } from '@aymoai/shared';

import { appelle, type Issue } from './appel.ts';

export type { Issue };

/** Un projet, tel que la liste et la fiche le rendent. */
export interface ProjetVu {
  readonly id: string;
  readonly nom: string;

  /**
   * Le référent du projet (`D-023`), quand il y en a un. L'écran DOIT le
   * nommer : sans ce mot affiché, quelqu'un suivrait le projet sans que les
   * participants le sachent, et `D-021` — « il ne lit rien en silence » —
   * serait violée en silence, précisément.
   */
  readonly referentId?: string;

  /** Absente quand le projet n'en a pas — jamais une chaîne vide. */
  readonly description?: string;

  readonly version: number;
  readonly archive: boolean;
  readonly niveau: 'proprietaire' | 'editeur' | 'lecteur';
}

/** Un membre d'un projet, tel que la liste le rend. */
export interface MembreVu {
  readonly compteId: string;
  readonly adresseElectronique: string;
  readonly nomAffiche: string;
  readonly niveau: 'proprietaire' | NiveauDeMembre;
}

/** Qui l'on est, tel que le serveur le dit. */
export interface ProfilVu {
  /** Son propre identifiant — pour reconnaître SA ligne dans une liste. */
  readonly compteId: string;

  readonly adresseElectronique: string;
  readonly nomAffiche: string;
  readonly role: RoleDeCompte;
}

/**
 * Un référent possible (`D-031`) : un nom, et de quoi le désigner. **Rien
 * d'autre ne traverse** — le serveur ne rend ni adresse, ni rôle, ni état de
 * compte, et l'écran n'a donc rien à cacher lui-même.
 */
export interface ReferentPossibleVu {
  readonly compteId: string;
  readonly nomAffiche: string;
}

/** Le profil du compte connecté — c'est aussi la façon de savoir si on l'est. */
export function litLeProfil() {
  return appelle<ProfilVu>('/api/profil');
}

/** Entrer. Le serveur pose le cookie ; le client ne le voit jamais. */
export function seConnecte(adresseElectronique: string, motDePasse: string) {
  return appelle<ProfilVu>('/api/connexion', {
    methode: 'POST',
    corps: { adresseElectronique, motDePasse },
  });
}

/**
 * Sortir. Le serveur révoque la session et efface le cookie ; le client, lui,
 * ne peut pas effacer ce cookie — il est `HttpOnly`. C'est pourquoi se
 * déconnecter est un APPEL et non un geste local : effacer côté client
 * laisserait la session vivante au serveur, et un simple rechargement la
 * retrouverait.
 */
export function seDeconnecte() {
  return appelle<undefined>('/api/deconnexion', { methode: 'POST' });
}

/**
 * L'adresse du fichier de ses propres données — rendue par une fonction, pas
 * posée en dur dans l'écran, pour que le contrôle des portes voie le chemin.
 *
 * POURQUOI CE N'EST PAS UN APPEL. Tout le reste du client passe par `appelle`,
 * qui lit la réponse et la traduit. Ici, la réponse ne doit PAS être lue par le
 * client : elle doit atterrir dans les téléchargements. Un lien ordinaire fait
 * cela sans une ligne de code, il s'atteint au clavier, un lecteur d'écran
 * l'annonce comme un lien, et il ne charge pas plusieurs mégaoctets dans la
 * mémoire de la page pour les recracher aussitôt.
 *
 * CE QU'IL COÛTE, écrit franchement : un lien n'a AUCUN chemin d'erreur. Si le
 * serveur refuse, la page ne le saura pas. C'est pourquoi l'écran annonce ce
 * qui a été DEMANDÉ et jamais ce qui a réussi, et pourquoi la notice dit où
 * regarder si rien n'arrive.
 */
export function adresseDeLExportDeMesDonnees() {
  return '/api/moi/export';
}

/** Ce qu'un départ laisse derrière lui, compté avant et après. */
export interface ComptageDesTracesVu {
  readonly comptes: number;
  readonly sessions: number;
  readonly invitationsEmises: number;
  readonly invitationsRecues: number;
  readonly droitsDeProjet: number;
}

/** Le bilan que rend la suppression : les mêmes traces, avant et après. */
export interface BilanDeSuppressionVu {
  readonly avant: ComptageDesTracesVu;
  readonly apres: ComptageDesTracesVu;
}

/**
 * Partir pour de bon. Le mot de passe est exigé en preuve de possession : le
 * cookie prouve qu'une session est ouverte, jamais que la personne devant
 * l'écran est celle qui l'a ouverte — et un ordinateur familial reste allumé.
 *
 * Le serveur peut refuser pour deux raisons qui ne se confondent pas : ce
 * compte a fondé la famille, ou il est le dernier à l'administrer. L'écran rend
 * le refus tel quel, il n'en invente aucun.
 */
export function supprimeMonCompte(motDePasse: string) {
  return appelle<BilanDeSuppressionVu>('/api/moi/suppression', {
    methode: 'POST',
    corps: { motDePasse },
  });
}

/**
 * Demander un lien de récupération. Le résultat est le MÊME que l'adresse ait
 * un compte ou non — c'est voulu côté serveur, et l'écran n'a rien à en
 * déduire.
 */
export function demandeUnLienDOubli(adresseElectronique: string) {
  return appelle<{ fait: true }>('/api/oubli', {
    methode: 'POST',
    corps: { adresseElectronique },
  });
}

/** Poser un nouveau mot de passe avec le jeton reçu par courriel. */
export function poseLeNouveauMotDePasse(jeton: string, motDePasse: string) {
  return appelle<{ fait: true }>('/api/mot-de-passe', {
    methode: 'POST',
    corps: { jeton, motDePasse },
  });
}

/** Créer son compte depuis une invitation — la seule porte de naissance. */
export function creeSonCompte(jeton: string, nomAffiche: string, motDePasse: string) {
  return appelle<ProfilVu>('/api/inscription', {
    methode: 'POST',
    corps: { jeton, nomAffiche, motDePasse },
  });
}

/** Un compte de la famille, tel que l'administrateur le voit. */
export interface CompteDeLaFamille {
  readonly id: string;
  readonly adresseElectronique: string;
  readonly nomAffiche: string;
  readonly role: RoleDeCompte;
  readonly actif: boolean;

  /** Le compte qui a fondé la famille : aucun geste de l'écran ne le ferme. */
  readonly fondateur: boolean;

  readonly creeLe: string;
  readonly misAJourLe: string;
}

/** Une invitation, telle que la liste la rend. */
export interface InvitationVue {
  readonly id: string;
  readonly adresseInvitee: string;
  readonly roleAttribue: RoleDeCompte;
  readonly creeLe: string;
  readonly expireLe: string;
  readonly etat: 'active' | 'utilisee' | 'revoquee' | 'expiree';
}

/** La famille entière — parent administrateur seulement. */
export function listeLaFamille() {
  return appelle<{ comptes: CompteDeLaFamille[] }>('/api/comptes');
}

/** Fermer ou rouvrir un accès. */
export function changeLAcces(id: string, actif: boolean) {
  return appelle<{ actif: boolean; change: boolean }>(`/api/comptes/${id}`, {
    methode: 'PATCH',
    corps: { actif },
  });
}

/** Les invitations, toutes époques et tous états confondus. */
export function listeLesInvitations() {
  return appelle<InvitationVue[]>('/api/invitations');
}

/** Inviter quelqu'un — le serveur envoie le courriel et dit ce qu'il en a fait. */
export function invite(adresseInvitee: string, roleAttribue: RoleDeCompte) {
  return appelle<{
    adresseInvitee: string;
    jeton: string;
    expireLe: string;
    courriel: 'envoye' | 'echec_envoi' | 'non_configure';
  }>('/api/invitations', { methode: 'POST', corps: { adresseInvitee, roleAttribue } });
}

/** Annuler une invitation encore vivante. */
export function revoqueLInvitation(id: string) {
  return appelle<void>(`/api/invitations/${id}`, { methode: 'DELETE' });
}
